import { useCallback, useMemo } from 'react'
import TemplateClassic from './TemplateClassic'
import TemplateOverlay from './TemplateOverlay'
import TemplateDetailed from './TemplateDetailed'
import GridQuotePrice from './grid-quote-price'
import { GridCardCtx, type SharedCardOptions } from './card-parts'
import { writePartyParams } from '../occupancy'
import { resolvePropertyLink, withStayParams, type PropertyHrefFeed } from '../property-href'
import { usePropertyHrefFeed } from '../use-property-href-feed'
import { useWidget } from '../../providers/widget'
import { useFetchWidget } from '../../hooks'
import { pickUnitGroupSlug } from './unit-group'
import { compareRootProps, type CardCompare } from './card-compare'
import type { ExploreProperty, QuoteRateplan } from '../../widgets/explorer/data'
import type { ExploreWidgetConfig, VisitorFilter } from '../../widgets/explorer/config'
import type { MapActions } from '../../widgets/explorer/providers'
import './property-card.css'

const TEMPLATES = {
  classic: TemplateClassic,
  overlay: TemplateOverlay,
  detailed: TemplateDetailed,
} as const

export type GridCardTemplate = keyof typeof TEMPLATES

export function isGridCardTemplate(value: unknown): value is GridCardTemplate {
  return typeof value === 'string' && value in TEMPLATES
}

/** Explorer card options mapped onto the shared template contract. */
export function mapExplorerCardOptions(options: ExploreWidgetConfig): SharedCardOptions {
  const card = options.card
  return {
    showImage: true,
    showPrice: options.priceDisplay.noDates !== 'none' || options.priceDisplay.withDates !== 'none',
    showLocation: card?.showLocation !== false,
    showFeatures: card?.showFeatures !== false,
    showPropertyType: card?.showType !== false,
    showExcerpt: false,
    imageAspectRatio: card?.imageAspectRatio ?? '16/9',
    maxImages: card?.photosLimit ?? 7,
    overlayGradientStrength: 'soft',
    openInNewTab: card?.openInNewTab === true,
    language: options.language,
    // #1262: the explorer's own translations group carries the shared card
    // keys, so the templates resolve against it — operator value → shipped
    // locale table → en_US default — exactly like the standalone card.
    translations: options.translations,
    // #1813: the Tier Rating ladder; undefined when unconfigured, so the
    // templates render no badge node.
    tierRating: (card as { tierRating?: SharedCardOptions['tierRating'] } | undefined)?.tierRating,
    // Card tags (#1812): the grid's pills in the type chip; absent = today's chip.
    cardTags: card?.cardTags,
  }
}

interface GridTemplateCardProps {
  template: GridCardTemplate
  property: ExploreProperty
  options: ExploreWidgetConfig
  visitorFilters?: VisitorFilter
  quoteData?: { rateplan: QuoteRateplan; nights: number } | null
  quoteLoading?: boolean
  quoteFailed?: boolean
  showFavoriteButton?: boolean
  mapActions?: MapActions | null
  /** Compare Homes (#1713): set only while compare mode is on. */
  compare?: CardCompare | null
}

/**
 * The property URL with the visitor's filter params, for a given slug —
 * matching TemplateExplorer's buildPropertyUrl (which stays untouched until
 * #902 retires the legacy card): dates + the whole party, pets included
 * (common/occupancy). Extracted from the property's own slug so the click
 * handler can rebuild it for a randomly-picked sibling (#1070).
 *
 * #1714: the base is the shared resolver's link, so a property the feed maps
 * to its own page (External Property Pages) links there, with the same stay
 * params after it unless the feed turns `carryStay` off.
 */
export function buildGridPropertyUrl(
  feed: PropertyHrefFeed | null | undefined,
  pdpRoute: string,
  slug: string | null,
  options: ExploreWidgetConfig,
  visitorFilters?: VisitorFilter,
): { href: string; external: boolean } {
  if (!slug) return { href: '#', external: false }
  const link = resolvePropertyLink({ feed, slug, pdpRoute })
  if (!visitorFilters) return link

  const params = new URLSearchParams()
  if (visitorFilters.checkin && visitorFilters.checkout) {
    params.set('checkin', visitorFilters.checkin)
    params.set('checkout', visitorFilters.checkout)
  }
  writePartyParams(params, {
    adults: visitorFilters.adults,
    children: visitorFilters.children,
    pets: options.showPetsFilter === false ? undefined : visitorFilters.pets,
  })

  return { href: withStayParams(link, params.toString()), external: link.external }
}

/**
 * The card's href (own slug) plus `pickHref` — a function that, called at
 * CLICK TIME, re-resolves the URL to a uniformly random sibling (#1070,
 * central#3210 unit_group). `pickUnitGroupSlug` is a no-op when the property
 * has no unit_group, so `pickHref()` always returning the current `href` in
 * that case is the "null = unchanged" behavior, not a special case here.
 */
function useGridPropertyUrl(
  property: ExploreProperty,
  options: ExploreWidgetConfig,
  visitorFilters?: VisitorFilter,
): { href: string; external: boolean; pickHref: () => string } {
  const { feedId, widgetId } = useWidget()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)

  const buildFor = useCallback(
    (slug: string | null) => buildGridPropertyUrl(hrefFeed, pdpRoute, slug, options, visitorFilters),
    [hrefFeed, pdpRoute, options.showPetsFilter, visitorFilters],
  )
  const own = useMemo(() => buildFor(property.slug), [buildFor, property.slug])
  const pickHref = useCallback(() => buildFor(pickUnitGroupSlug(property)).href, [buildFor, property])

  return { href: own.href, external: own.external, pickHref }
}

/**
 * Renders one of the shared property-card templates as an explorer grid card.
 * The wrapper carries the .hr-property-card scope class (the template
 * stylesheet nests everything under it) and keeps the map hover sync the
 * explorer card has. GridCardCtx threads the grid-only behavior — the
 * filter-carrying URL, the favorites heart, and quote-aware pricing — into
 * the shared parts without the standalone widget seeing any of it.
 */
export default function GridTemplateCard({
  template,
  property,
  options,
  visitorFilters,
  quoteData,
  quoteLoading,
  quoteFailed,
  showFavoriteButton,
  mapActions,
  compare,
}: GridTemplateCardProps) {
  const Template = TEMPLATES[template]
  const { href, external, pickHref } = useGridPropertyUrl(property, options, visitorFilters)
  const grid = useMemo(() => ({
    href,
    external,
    pickHref,
    showFavorite: showFavoriteButton === true,
    compare: compare ?? null,
    price: (
      <GridQuotePrice
        property={property}
        options={options}
        visitorFilters={visitorFilters}
        quoteData={quoteData}
        quoteLoading={quoteLoading}
        quoteFailed={quoteFailed}
      />
    ),
  }), [href, external, pickHref, showFavoriteButton, compare, property, options, visitorFilters, quoteData, quoteLoading, quoteFailed])
  return (
    <div
      className="property-item hr-property-card"
      data-id={property.id}
      onMouseEnter={() => mapActions?.focusMarker(property.id)}
      onMouseLeave={() => mapActions?.blurMarker(property.id)}
      {...compareRootProps(compare)}
    >
      <GridCardCtx.Provider value={grid}>
        <Template property={property} options={mapExplorerCardOptions(options)} />
      </GridCardCtx.Provider>
    </div>
  )
}
